/**
 * Export/Import Controls Component
 * Adapted from CirculatorWebpage ExportImportControls.tsx
 * Uses Material-UI instead of Shadcn UI
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Typography,
} from '@mui/material';

import { useFixedRoutes } from '../../context/FixedRoutesContext';
import { exportToJSON, exportToCSV, exportToExcel, downloadFile } from '../../utils/fixedRoutes/exportHelpers';
import {
  importFromJSON,
  extractStops,
  extractRoutes,
  validateImportedData,
} from '../../utils/fixedRoutes/importHelpers';
import { generateTimetable } from '../../utils/fixedRoutes/timetableUtils';
import { EXPORT_FILE_PREFIX } from '../../config/constants';
import { loadGTFSDataFromFile, convertGTFSFeedToFixedRoutes, summarizeServiceWindows, type ServicePreference } from '../../utils/fixedRoutes/gtfsConverter';
import { logEvent, trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';
import { CircularProgress, Select, MenuItem, Stack } from '@mui/material';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { ExportGtfsModal } from './ExportGtfsModal';
import { ExportBodsModal } from './ExportBodsModal';
import { convertTransXChangeToGtfs } from '../../services/network/networkApi';

/** One export/import/danger row: icon tile + title/subtitle + action button. */
function DataActionRow({
  iconName, tint, iconColor, title, subtitle, actionLabel, actionIcon, onClick, disabled = false, disabledTitle, danger = false,
}: {
  iconName: string;
  tint: string;
  iconColor: string;
  title: string;
  subtitle: string;
  actionLabel: string;
  actionIcon: string;
  onClick: () => void;
  disabled?: boolean;
  disabledTitle?: string;
  danger?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: danger ? 'error.light' : 'divider',
        borderRadius: 2,
        backgroundColor: danger ? 'rgba(220, 38, 38, 0.04)' : 'background.paper',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tint,
        }}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- @liftango/ui-icons name type doesn't survive being passed through a variable prop */}
        <Icon name={iconName as any} size={20} style={{ color: iconColor }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      </Box>
      <Button
        variant="outlined"
        size="small"
        color={danger ? 'error' : 'primary'}
        startIcon={<Icon name={actionIcon as any} size={20} />}
        onClick={onClick}
        disabled={disabled}
        title={disabled ? disabledTitle : undefined}
        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
      >
        {actionLabel}
      </Button>
    </Box>
  );
}

/** Uppercase section label above a group of data rows. */
function DataSectionLabel({ label, danger = false }: { label: string; danger?: boolean }) {
  return (
    <Typography
      variant="overline"
      sx={{ fontWeight: 700, letterSpacing: '0.08em', color: danger ? 'error.main' : 'text.secondary' }}
    >
      {label}
    </Typography>
  );
}

export function ExportImportControls() {
  const { t } = useTranslation('fixedRoutes');
  const {
    stops,
    routes,
    trafficMultiplier,
    addStop,
    addRoute,
    updateRoute,
    bulkAddStopsRoutes,
    setTrafficMultiplier,
    clearAllData,
  } = useFixedRoutes();

  const [isClearDialogOpen, setIsClearDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isGTFSImportDialogOpen, setIsGTFSImportDialogOpen] = useState(false);
  const [isGTFSExportDialogOpen, setIsGTFSExportDialogOpen] = useState(false);
  const [isBodsExportDialogOpen, setIsBodsExportDialogOpen] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importMode, setImportMode] = useState<'replace' | 'merge'>('replace');
  const [gtfsLoading, setGtfsLoading] = useState(false);
  const [gtfsProgress, setGtfsProgress] = useState(0);
  const [gtfsServiceWindows, setGtfsServiceWindows] = useState<Array<{ serviceId: string; label: string }>>([]);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [gtfsPreferredService, setGtfsPreferredService] = useState<ServicePreference>('weekday');

  const handleExportJSON = () => {
    const start = Date.now();
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_start, {
      export_type: 'json',
      export_source: 'fixed_routes',
      item_count: routes.length,
      timestamp: new Date().toISOString(),
    });
    try {
      const state = { stops, routes, trafficMultiplier };
      const json = exportToJSON(state);
      const filename = `${EXPORT_FILE_PREFIX}-${new Date().toISOString().split('T')[0]}.json`;
      downloadFile(json, filename, 'application/json');
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_complete, {
        export_type: 'json',
        export_source: 'fixed_routes',
        item_count: routes.length,
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_fail, {
        feature_area: 'export',
        error_category: 'export_error',
        error_code: error instanceof Error ? error.name : 'unknown_error',
        retryable: true,
        export_type: 'json',
        export_source: 'fixed_routes',
        item_count: routes.length,
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleExportCSV = () => {
    const start = Date.now();
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_start, {
      export_type: 'csv',
      export_source: 'fixed_routes',
      item_count: routes.length,
      timestamp: new Date().toISOString(),
    });
    try {
      const csv = exportToCSV(routes, stops, trafficMultiplier);
      const filename = `${EXPORT_FILE_PREFIX}-${new Date().toISOString().split('T')[0]}.csv`;
      downloadFile(csv, filename, 'text/csv');
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_complete, {
        export_type: 'csv',
        export_source: 'fixed_routes',
        item_count: routes.length,
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_fail, {
        feature_area: 'export',
        error_category: 'export_error',
        error_code: error instanceof Error ? error.name : 'unknown_error',
        retryable: true,
        export_type: 'csv',
        export_source: 'fixed_routes',
        item_count: routes.length,
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleExportExcel = () => {
    const start = Date.now();
    trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_start, {
      export_type: 'excel',
      export_source: 'fixed_routes',
      item_count: routes.length,
      timestamp: new Date().toISOString(),
    });
    try {
      exportToExcel(routes, stops, trafficMultiplier);
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_complete, {
        export_type: 'excel',
        export_source: 'fixed_routes',
        item_count: routes.length,
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.export_fail, {
        feature_area: 'export',
        error_category: 'export_error',
        error_code: error instanceof Error ? error.name : 'unknown_error',
        retryable: true,
        export_type: 'excel',
        export_source: 'fixed_routes',
        item_count: routes.length,
        duration_ms: Date.now() - start,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleImportJSON = async (file: File) => {
    try {
      setImportError(null);
      const data = await importFromJSON(file);

      if (!validateImportedData(data)) {
        throw new Error(t('exportImport.errors.invalidDataFormat'));
      }

      if (importMode === 'replace') {
        // Clear existing data first
        clearAllData();
      }

      // Import stops
      const importedStops = extractStops(data);
      importedStops.forEach((stop) => {
        if (importMode === 'merge') {
          // Check if stop already exists
          const exists = stops.find((s) => s.id === stop.id);
          if (!exists) {
            addStop(stop);
          }
        } else {
          addStop(stop);
        }
      });

      // Import routes
      const importedRoutes = extractRoutes(data);
      importedRoutes.forEach((route) => {
        if (importMode === 'merge') {
          const exists = routes.find((r) => r.id === route.id);
          if (exists) {
            updateRoute(route);
          } else {
            addRoute(route);
          }
        } else {
          addRoute(route);
        }
      });

      // Import traffic multiplier
      if (data.trafficMultiplier && data.trafficMultiplier > 0) {
        setTrafficMultiplier(data.trafficMultiplier);
      }

      setIsImportDialogOpen(false);
      setImportError(null);
    } catch (error) {
      setImportError(error instanceof Error ? error.message : t('exportImport.errors.importFailed'));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.endsWith('.json')) {
        handleImportJSON(file);
      } else {
        setImportError(t('exportImport.errors.onlyJsonSupported'));
      }
    }
    // Reset input
    e.target.value = '';
  };

  const handleClearAll = () => {
    clearAllData();
    setIsClearDialogOpen(false);
    // Optionally reload the page to reset state completely
    // window.location.reload();
  };

  const processGTFSFile = useCallback(async (file: File, mode: 'replace' | 'merge' = 'merge') => {
    if (!file || !(file.name.toLowerCase().endsWith('.zip') || file.name.toLowerCase().endsWith('.xml'))) {
      setImportError(t('exportImport.errors.selectGtfsZip'));
      return;
    }

    // In-browser import loads + parses the whole feed in the tab; a national BODS
    // archive (e.g. ~1.5 GB, hundreds of nested operator zips) will freeze it. Guard
    // it and point at the right path rather than hanging at "processing".
    const MAX_IN_BROWSER_BYTES = 150 * 1024 * 1024;
    if (file.size > MAX_IN_BROWSER_BYTES) {
      setImportError(
        t('exportImport.errors.fileTooLarge', {
          defaultValue:
            'This file is {{mb}} MB — too large to import in the browser. Use a single-operator or single-region BODS/GTFS file. (National BODS archives belong in the hosted network store, and Spatial Analysis already pulls BODS for a drawn area automatically.)',
          mb: Math.round(file.size / 1024 / 1024),
        }),
      );
      return;
    }

    setGtfsLoading(true);
    setGtfsProgress(0);
    setImportError(null);

    const startTime = Date.now();

    try {
      logEvent({
        category: 'Fixed Routes',
        action: 'GTFSImportInitiated',
        label: file.name,
        file_size: file.size,
        file_size_mb: Math.round(file.size / 1024 / 1024 * 100) / 100,
        import_mode: mode,
      });

      // Stream progress updates for better UX on large files
      setGtfsProgress(10);
      // Accept BODS too. A .xml is TransXChange → convert to GTFS via the backend.
      // A .zip may be GTFS (import directly) OR a TransXChange zip → try GTFS first,
      // and if it yields no usable GTFS tables, convert via the backend and retry.
      const lowerName = file.name.toLowerCase();
      let gtfsData: Awaited<ReturnType<typeof loadGTFSDataFromFile>>;
      if (lowerName.endsWith('.xml')) {
        gtfsData = await loadGTFSDataFromFile(await convertTransXChangeToGtfs(file));
      } else {
        try {
          gtfsData = await loadGTFSDataFromFile(file);
          if (!gtfsData.routes || gtfsData.routes.length === 0) {
            throw new Error('No GTFS routes in zip');
          }
        } catch (gtfsErr) {
          // Likely a TransXChange .zip — convert via the backend, then import.
          gtfsData = await loadGTFSDataFromFile(await convertTransXChangeToGtfs(file));
        }
      }

      setGtfsProgress(30);
      const summaries = summarizeServiceWindows(gtfsData.calendar, gtfsData.calendarDates);
      setGtfsServiceWindows(summaries.map(s => ({ serviceId: s.serviceId, label: s.label })));

      // Auto-select service if only one option
      if (summaries.length === 1) {
        setSelectedServiceId(summaries[0].serviceId);
      } else if (summaries.length > 1) {
        // Find weekday service or use first
        const weekdayService = summaries.find(s => s.preference === 'weekday');
        const defaultServiceId = weekdayService?.serviceId || summaries[0].serviceId;
        setSelectedServiceId(defaultServiceId);
      }

      setGtfsProgress(50);
      
      // For large imports, show progress during conversion
      const isLargeImport = gtfsData.stops.length > 5000 || gtfsData.routes.length > 1000;
      const result = convertGTFSFeedToFixedRoutes(gtfsData, {
        preferredService: gtfsPreferredService,
        targetServiceId: selectedServiceId || undefined,
      });

      setGtfsProgress(70);
      
      // Bulk add with chunking (handled internally for large imports)
      bulkAddStopsRoutes(result.stops, result.routes, mode);

      // Wait a bit for chunking to complete on large imports
      if (isLargeImport) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setGtfsProgress(100);

      logEvent({
        category: 'Fixed Routes',
        action: 'GTFSImportSuccess',
        label: file.name,
        stops_created: result.stats.totalStops,
        routes_created: result.stats.totalRoutes,
        routes_skipped: result.stats.skippedRoutes,
        warnings_count: result.warnings.length,
        processing_time_ms: Date.now() - startTime,
        import_mode: mode,
      });

      setIsGTFSImportDialogOpen(false);
      setImportError(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('exportImport.errors.unknownError');
      setImportError(t('exportImport.errors.gtfsImportFailed', { message: errorMessage }));

      logEvent({
        category: 'Fixed Routes',
        action: 'GTFSImportError',
        label: file.name,
        error_message: errorMessage,
        processing_time_ms: Date.now() - startTime,
        import_mode: mode,
      });
    } finally {
      setGtfsLoading(false);
      setGtfsProgress(0);
    }
  }, [gtfsPreferredService, selectedServiceId, bulkAddStopsRoutes]);

  const handleGTFSFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await processGTFSFile(file, importMode);
      // Reset input
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFF3E8' }}>
          <Icon name="file-import" size={20} style={{ color: '#EA580C' }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {t('exportImport.data.heading')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('exportImport.data.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Export */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DataSectionLabel label={t('exportImport.data.sectionExport')} />
        <DataActionRow iconName="rectangle-list" tint="#EEF2FF" iconColor="#6366F1" title={t('exportImport.exportJson')} subtitle={t('exportImport.data.jsonSubtitle')} actionLabel={t('exportImport.data.export')} actionIcon="file-arrow-down" onClick={handleExportJSON} />
        <DataActionRow iconName="table" tint="#ECFDF3" iconColor="#16A34A" title={t('exportImport.exportCsv')} subtitle={t('exportImport.data.csvSubtitle')} actionLabel={t('exportImport.data.export')} actionIcon="file-arrow-down" onClick={handleExportCSV} />
        <DataActionRow iconName="table-cells-large" tint="#ECFDF3" iconColor="#15803D" title={t('exportImport.exportExcel')} subtitle={t('exportImport.data.excelSubtitle')} actionLabel={t('exportImport.data.export')} actionIcon="file-arrow-down" onClick={handleExportExcel} />
        <DataActionRow iconName="bus" tint="#FFF3E8" iconColor="#EA580C" title={t('exportImport.exportGtfs')} subtitle={t('exportImport.data.gtfsSubtitle')} actionLabel={t('exportImport.data.export')} actionIcon="file-arrow-down" onClick={() => setIsGTFSExportDialogOpen(true)} disabled={routes.length === 0} disabledTitle={t('exportImport.exportGtfsDisabled', 'Add routes to export a GTFS feed')} />
        <DataActionRow iconName="file-signature" tint="#F3F4F6" iconColor="#6B7280" title={t('exportImport.data.bodsTitle')} subtitle={t('exportImport.data.bodsSubtitle')} actionLabel={t('exportImport.data.export')} actionIcon="file-arrow-down" onClick={() => setIsBodsExportDialogOpen(true)} disabled={routes.length === 0} disabledTitle={t('exportImport.exportBodsDisabled', 'Add routes to export to BODS')} />
      </Box>

      {/* Import */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DataSectionLabel label={t('exportImport.data.sectionImport')} />
        <DataActionRow iconName="file-import" tint="#F3EEFF" iconColor="#7C3AED" title={t('exportImport.import')} subtitle={t('exportImport.data.importSubtitle')} actionLabel={t('exportImport.data.chooseFile')} actionIcon="upload" onClick={() => setIsImportDialogOpen(true)} />
        <DataActionRow iconName="bus" tint="#FFF3E8" iconColor="#EA580C" title={t('exportImport.importGtfs')} subtitle={t('exportImport.data.importGtfsSubtitle')} actionLabel={t('exportImport.data.chooseZip')} actionIcon="upload" onClick={() => setIsGTFSImportDialogOpen(true)} />
        <Box sx={{ display: 'flex', gap: 1, p: 1.5, borderRadius: 2, backgroundColor: 'rgba(99, 102, 241, 0.06)' }}>
          <Box sx={{ flexShrink: 0, mt: '2px' }}>
            <Icon name="circle-info" size={20} style={{ color: '#6366F1' }} />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {t('exportImport.gtfsDescription')}
          </Typography>
        </Box>
      </Box>

      {/* Danger zone */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <DataSectionLabel label={t('exportImport.data.sectionDanger')} danger />
        <DataActionRow iconName="trash" tint="#FEECEC" iconColor="#DC2626" title={t('exportImport.data.clearTitle')} subtitle={t('exportImport.data.clearSubtitle')} actionLabel={t('exportImport.data.clearButton')} actionIcon="trash" onClick={() => setIsClearDialogOpen(true)} danger />
      </Box>

      {/* Import Dialog */}
      <Dialog
        open={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('exportImport.dialogs.importTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('exportImport.dialogs.importDescription')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t('exportImport.dialogs.importMode')}</FormLabel>
              <RadioGroup
                value={importMode}
                onChange={(e) => setImportMode(e.target.value as 'replace' | 'merge')}
              >
                <FormControlLabel
                  value="replace"
                  control={<Radio />}
                  label={t('exportImport.dialogs.replace')}
                />
                <FormControlLabel
                  value="merge"
                  control={<Radio />}
                  label={t('exportImport.dialogs.merge')}
                />
              </RadioGroup>
            </FormControl>
            <Box>
              <Typography variant="body2" gutterBottom>
                {t('exportImport.dialogs.selectJson')}
              </Typography>
              <input
                id="import-file"
                type="file"
                accept=".json"
                onChange={handleFileSelect}
                style={{ width: '100%' }}
              />
            </Box>
            {importError && (
              <Alert severity="error" icon={<Icon size={20} name="triangle-exclamation" />}>
                <AlertTitle>{t('exportImport.dialogs.importErrorTitle')}</AlertTitle>
                {importError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsImportDialogOpen(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* GTFS Import Dialog */}
      <Dialog
        open={isGTFSImportDialogOpen}
        onClose={() => !gtfsLoading && setIsGTFSImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('exportImport.dialogs.gtfsImportTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('exportImport.dialogs.gtfsImportDescription')}
            </Typography>

            <FormControl component="fieldset">
              <FormLabel component="legend">{t('exportImport.dialogs.importMode')}</FormLabel>
              <RadioGroup
                value={importMode}
                onChange={(e) => setImportMode(e.target.value as 'replace' | 'merge')}
              >
                <FormControlLabel
                  value="replace"
                  control={<Radio />}
                  label={t('exportImport.dialogs.replace')}
                />
                <FormControlLabel
                  value="merge"
                  control={<Radio />}
                  label={t('exportImport.dialogs.merge')}
                />
              </RadioGroup>
            </FormControl>

            {gtfsServiceWindows.length > 1 && (
              <FormControl fullWidth>
                <FormLabel>{t('exportImport.dialogs.serviceWindow')}</FormLabel>
                <Select
                  value={selectedServiceId}
                  onChange={(e) => setSelectedServiceId(e.target.value)}
                  disabled={gtfsLoading}
                >
                  {gtfsServiceWindows.map((sw) => (
                    <MenuItem key={sw.serviceId} value={sw.serviceId}>
                      {sw.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box>
              <input
                id="gtfs-import-file"
                type="file"
                accept=".zip,.xml"
                onChange={handleGTFSFileSelect}
                style={{ display: 'none' }}
                disabled={gtfsLoading}
              />
              <label htmlFor="gtfs-import-file">
                <Button
                  variant="contained"
                  component="span"
                  disabled={gtfsLoading}
                  fullWidth
                >
                  {t('exportImport.dialogs.selectGtfsZip')}
                </Button>
              </label>
            </Box>

            {gtfsLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress variant="determinate" value={gtfsProgress} size={24} />
                <Typography variant="body2">
                  {t('exportImport.dialogs.processing', { percent: Math.round(gtfsProgress) })}
                </Typography>
              </Box>
            )}

            {importError && (
              <Alert severity="error" icon={<Icon size={20} name="triangle-exclamation" />}>
                <AlertTitle>{t('exportImport.dialogs.importErrorTitle')}</AlertTitle>
                {importError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsGTFSImportDialogOpen(false)} disabled={gtfsLoading}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Data Dialog */}
      <Dialog
        open={isClearDialogOpen}
        onClose={() => setIsClearDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('exportImport.dialogs.clearTitle')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" icon={<Icon size={20} name="triangle-exclamation" />}>
            <AlertTitle>{t('common.warning')}</AlertTitle>
            {t('exportImport.dialogs.clearWarning')}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsClearDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleClearAll}
            color="error"
            variant="contained"
          >
            {t('exportImport.clearAll')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* GTFS Export Modal */}
      <ExportGtfsModal
        open={isGTFSExportDialogOpen}
        onClose={() => setIsGTFSExportDialogOpen(false)}
      />

      {/* BODS (TransXChange) Export Modal */}
      <ExportBodsModal
        open={isBodsExportDialogOpen}
        onClose={() => setIsBodsExportDialogOpen(false)}
      />

    </Box>
  );
}

